/* One host: who they are, what their strand is, when they are next on, and
 * every set they have broadcast.
 *
 * ARCHIVED IS NOT DELETED, AND THIS PAGE IS WHERE THAT PROMISE IS KEPT.
 * `status: 'archived'` means a host is no longer on the schedule — they moved
 * city, the residency ended, life happened. The API serves them exactly like an
 * active host and so does this page: the bio stays, the sets stay, the credits
 * on forty episodes keep resolving to a real page instead of a 404. What
 * changes is one honest band near the top saying they are not currently on the
 * schedule, in the past tense, without an apology and without a tombstone.
 * A station that deletes its lapsed hosts is deleting its own history.
 *
 * The one thing this page must never do is invent. A host with no bio gets a
 * sentence saying we have not written one, not a generated paragraph; a host
 * with no bookings gets "nothing booked", not a fake date.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, isAbortError } from '../api';
import {
  Artwork,
  Chip,
  ChipList,
  ErrorNote,
  Section,
  Spinner,
  hostNames,
} from '../components';
import { formatDuration, formatFullDate, formatTimeRange, readTzMode, tzLabel } from '../format';
import { usePlayer } from '../player';
import type { EpisodeCard, HostDetail } from '../types';

/** Sets drawn before the list folds. Enough to fill a screen, not a scroll. */
const SETS_SHOWN = 8;

/** Bookings drawn under the headline one. */
const UPCOMING_SHOWN = 4;

const LINK_LABELS: Record<string, string> = {
  instagram: 'Instagram',
  soundcloud: 'SoundCloud',
  bandcamp: 'Bandcamp',
  mixcloud: 'Mixcloud',
  discogs: 'Discogs',
  spotify: 'Spotify',
  youtube: 'YouTube',
  twitter: 'Twitter',
  website: 'Website',
};

/** `17H 22M`. Broadcast time is the number a host wants on their own page. */
function onAir(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0M';
  const total = Math.round(seconds / 60);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return hours > 0 ? `${hours}H ${minutes}M` : `${minutes}M`;
}

/** A website link reads better as its own domain than as the word "Website". */
function linkLabel(key: string, url: string): string {
  if (key !== 'website') return LINK_LABELS[key] ?? key.replace(/[-_]/g, ' ');
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return LINK_LABELS.website;
  }
}

/* ------------------------------------------------------------------ styles */

const CRUMB: CSSProperties = {
  padding: '14px clamp(12px, 4vw, 28px)',
  color: 'var(--muted)',
  borderBottom: 'var(--bw-hair) solid var(--edge)',
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
};

const HEAD: CSSProperties = {
  display: 'flex',
  gap: 'clamp(20px, 4vw, 44px)',
  padding: '32px clamp(12px, 4vw, 28px) 40px',
  borderBottom: 'var(--bw) solid var(--edge)',
  background: 'var(--paper)',
  flexWrap: 'wrap',
};

const HEAD_BODY: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  flexGrow: 1,
  minWidth: 0,
  flexBasis: '320px',
};

const BADGES: CSSProperties = { display: 'flex', gap: '10px', flexWrap: 'wrap' };

const BAND: CSSProperties = {
  borderBottom: 'var(--bw) solid var(--edge)',
  padding: '26px clamp(12px, 4vw, 28px)',
  display: 'flex',
  gap: 'clamp(18px, 3vw, 44px)',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const UPNEXT: CSSProperties = {
  ...BAND,
  background: 'var(--blue)',
  color: 'var(--on-blue)',
};

const UPNEXT_QUIET: CSSProperties = {
  ...BAND,
  background: 'var(--paper)',
};

const ARCHIVED: CSSProperties = {
  ...BAND,
  background: 'var(--surface)',
  boxShadow: 'inset 0 0 0 var(--bw-on) var(--edge)',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: '12px',
};

const BAND_DATE: CSSProperties = {
  fontSize: 'clamp(1.25rem, 3vw, 1.875rem)',
  lineHeight: 1,
  fontVariationSettings: "'wdth' 100, 'wght' 800",
  textTransform: 'uppercase',
};

/* -------------------------------------------------------------------- page */

export default function Host() {
  const { slug } = useParams<{ slug: string }>();
  const [tz] = useState(readTzMode);

  const [host, setHost] = useState<HostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const [setsOpen, setSetsOpen] = useState(false);
  const [playNote, setPlayNote] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setHost(null);
    setSetsOpen(false);
    setPlayNote(null);

    api
      .getHost(slug, controller.signal)
      .then((found) => {
        setHost(found);
        setLoading(false);
      })
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setError(failure);
        setLoading(false);
      });

    return () => controller.abort();
  }, [slug, attempt]);

  useEffect(() => {
    document.title = host ? `${host.name} — Higher Ground Radio` : 'Host — Higher Ground Radio';
  }, [host]);

  /* -------------------------------------------------------------- player */

  const player = usePlayer();
  const playerRef = useRef(player);
  playerRef.current = player;

  const playRef = useRef<AbortController | null>(null);
  useEffect(() => () => playRef.current?.abort(), []);

  /**
   * Walk a list of catalogue numbers and hand the persistent player the first
   * playable one. SoundCloud is preferred by the player; a legacy local file
   * remains a fallback during the archive cutover.
   */
  const play = useCallback(async (list: readonly string[]) => {
    playRef.current?.abort();
    const controller = new AbortController();
    playRef.current = controller;

    setPlayNote(null);
    setStarting(true);

    let skipped = 0;
    for (const catalogueNo of list) {
      try {
        const episode = await api.getEpisode(catalogueNo, controller.signal);
        if (!episode.soundcloud_url && !episode.audio_url) {
          skipped += 1;
          continue;
        }
        playerRef.current.playEpisode(episode);
        setStarting(false);
        if (skipped > 0) {
          setPlayNote(
            `Started at ${episode.catalogue_no}. The ${skipped} newer ${skipped === 1 ? 'set has' : 'sets have'} no SoundCloud track or local replay attached.`,
          );
        }
        return;
      } catch (failure: unknown) {
        if (isAbortError(failure)) return;
        skipped += 1;
      }
    }

    setStarting(false);
    setPlayNote(
      list.length === 0
        ? 'There is nothing here to play yet.'
        : 'None of these sets has a SoundCloud track or local replay attached yet.',
    );
  }, []);

  const playOne = useCallback(
    (episode: EpisodeCard) => {
      const current = playerRef.current;
      if (current.mode === 'episode' && current.current?.catalogue_no === episode.catalogue_no) {
        current.toggle();
        return;
      }
      void play([episode.catalogue_no]);
    },
    [play],
  );

  /* ---------------------------------------------------------- derivations */

  const episodes = useMemo(() => host?.episodes ?? [], [host]);

  /** How many sets belong to each named strand. Counted from what we have. */
  const showCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const episode of episodes) {
      if (!episode.show) continue;
      counts.set(episode.show.slug, (counts.get(episode.show.slug) ?? 0) + 1);
    }
    return counts;
  }, [episodes]);

  const links = useMemo(() => {
    const entries = Object.entries(host?.links ?? {}).filter(
      ([, url]) => typeof url === 'string' && url.trim().length > 0,
    );
    return entries;
  }, [host]);

  /* ---------------------------------------------------------------- view */

  if (loading) {
    return (
      <>
        <Section level={1} title={slug ?? 'Host'} meta="Loading" />
        <div style={{ padding: '34px clamp(12px, 4vw, 28px)' }}>
          <Spinner label="Loading this profile" />
        </div>
      </>
    );
  }

  if (error || !host) {
    return (
      <>
        <Section level={1} title="Host" meta={slug ?? ''} />
        <ErrorNote error={error} what="this profile" onRetry={() => setAttempt((n) => n + 1)} />
        <div
          style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            padding: '0 clamp(12px, 4vw, 28px) 40px',
          }}
        >
          <Link className="btn" to="/residents">
            Everyone on the roster
          </Link>
          <Link className="btn-ghost" to="/archive">
            The archive
          </Link>
        </div>
      </>
    );
  }

  const archived = host.status === 'archived';
  const shown = setsOpen ? episodes : episodes.slice(0, SETS_SHOWN);
  const headline = host.upcoming[0] ?? null;
  const laterOn = host.upcoming.slice(1, 1 + UPCOMING_SHOWN);

  return (
    <>
      <nav className="mono" style={CRUMB} aria-label="Breadcrumb">
        <Link to="/residents">Higher Ground Rhythm Police</Link>
        <span aria-hidden="true">→</span>
        <span aria-current="page">{host.name}</span>
      </nav>

      <div style={HEAD}>
        <div style={{ width: 'clamp(200px, 28vw, 380px)', flexShrink: 0 }}>
          <Artwork
            color={host.artwork_color}
            imageUrl={host.artwork_url}
            display={host.monogram}
            label={archived ? 'Archived' : host.is_resident ? 'Rhythm Police' : 'Guest'}
            size="hero"
          />
        </div>

        <div style={HEAD_BODY}>
          <div style={BADGES}>
            {/* Every accent has ONE fixed text rule and it is not negotiable per
                component: an accent field takes its own --on-accent, in both themes, or it turns
                unreadable the moment somebody's OS is dark. */}
            <span
              className="badge"
              style={
                host.is_resident
                  ? { background: 'var(--accent)', color: 'var(--on-accent)' }
                  : { background: 'var(--paper)' }
              }
            >
              {host.is_resident ? 'Rhythm Police' : 'Guest'}
            </span>
            {host.resident_rank ? (
              <span className="badge" style={{ background: 'var(--paper)' }}>
                {host.resident_rank}
              </span>
            ) : null}
            {archived ? (
              <span className="badge" style={{ background: 'var(--surface)' }}>
                Archived
              </span>
            ) : null}
            {host.resident_since ? (
              <span className="badge" style={{ background: 'var(--paper)' }}>
                Since {formatFullDate(host.resident_since, tz)}
              </span>
            ) : null}
            {host.pronouns ? (
              <span className="badge" style={{ background: 'var(--paper)' }}>
                {host.pronouns}
              </span>
            ) : null}
          </div>

          <h1 className="t-mega" style={{ fontSize: 'clamp(2.75rem, 8vw, 7rem)' }}>
            {host.name}
          </h1>

          <div className="mono-13 dim">
            {[
              host.city,
              `${host.stats.episode_count} ${host.stats.episode_count === 1 ? 'set' : 'sets'}`,
              `${onAir(host.stats.seconds_on_air)} on air`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>

          {host.bio ? (
            <p className="t-body">{host.bio}</p>
          ) : (
            <p className="t-body">
              We have not written a profile for {host.name} yet. The sets below are the record in the
              meantime, and they are the better half of it.
            </p>
          )}

          {host.genres.length > 0 ? (
            <ChipList label="Genres they play">
              {host.genres.map((genre) => (
                <Chip key={genre.slug} color={genre.color_token} to={`/genre/${genre.slug}`}>
                  {genre.name}
                </Chip>
              ))}
            </ChipList>
          ) : null}

          {links.length > 0 ? (
            <div className="mono dim" style={{ lineHeight: 1.9 }}>
              {links.map(([key, url], index) => (
                <span key={key}>
                  {index > 0 ? ' · ' : ''}
                  <a href={url} target="_blank" rel="noreferrer">
                    {linkLabel(key, url)}
                  </a>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {archived ? (
        <div style={ARCHIVED}>
          <div className="mono dim">No longer on the schedule</div>
          <p className="t-body">
            {host.name} does not currently hold a slot with us. Nothing has been removed: the sets
            below were broadcast, they stay in the archive, and every episode that credits{' '}
            {host.name} still links here. If that changes, this page changes with it.
          </p>
        </div>
      ) : null}

      {/* Their named strand, surfaced above the list of individual sets. A
          guest has no strand, and gets the plain version of the same band
          rather than an empty accent one. */}
      <div style={{ ...BAND, background: host.shows.length > 0 ? 'var(--accent)' : 'var(--surface)' }}>
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
          <div className="mono">
            {host.shows.length === 0
              ? 'In the archive'
              : host.shows.length === 1
                ? 'Their show'
                : 'Their shows'}
          </div>

          {host.shows.length === 0 ? (
            <>
              <div className="t-h2" style={{ fontSize: 'clamp(1.75rem, 5vw, 3rem)' }}>
                {host.stats.episode_count} {host.stats.episode_count === 1 ? 'set' : 'sets'}
              </div>
              <p className="t-body" style={{ maxWidth: '62ch' }}>
                {host.name} has no named strand on the station — these are one-offs, guest slots and
                takeovers, each one filed on its own.
              </p>
            </>
          ) : (
            host.shows.map((show) => {
              const count = showCounts.get(show.slug) ?? 0;
              return (
                <div
                  key={show.slug}
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  <h2 className="t-h2" style={{ fontSize: 'clamp(1.75rem, 5vw, 3.5rem)' }}>
                    {show.title}
                  </h2>
                  <div className="mono">
                    {count > 0
                      ? `${count} ${count === 1 ? 'episode' : 'episodes'} in the archive`
                      : 'No episodes filed under this strand yet'}
                    {' · '}
                    <Link to={`/archive?show=${encodeURIComponent(show.slug)}`}>
                      Browse the strand →
                    </Link>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ flexShrink: 0 }}>
          <button
            type="button"
            className="btn-lg"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              /* Paper on the accent band, the accent on the quiet one — and the field
                 is left to base.css when the button is disabled, or the
                 disabled treatment would be painted over. */
              ...(episodes.length === 0 || starting
                ? null
                : host.shows.length > 0
                  ? { background: 'var(--paper)', color: 'var(--text)' }
                  : { background: 'var(--accent)', color: 'var(--on-accent)' }),
            }}
            disabled={episodes.length === 0 || starting}
            onClick={() => void play(episodes.map((episode) => episode.catalogue_no))}
          >
            <span className="tri" aria-hidden="true" />
            {starting ? 'Starting…' : 'Play newest set'}
          </button>
        </div>
      </div>

      {playNote ? (
        <p
          className="mono dim"
          role="status"
          style={{ margin: 0, padding: '12px clamp(12px, 4vw, 28px)', lineHeight: 1.6 }}
        >
          {playNote}
        </p>
      ) : null}

      {/* Next on air. The .ics is one VEVENT straight off the API — a date
          somebody can actually keep, rather than one they have to retype. */}
      {headline ? (
        <div className="on-blue" style={UPNEXT}>
          <div className="mono" style={{ color: 'var(--accent)' }}>
            Next on air
          </div>
          <div style={BAND_DATE}>
            {formatFullDate(headline.starts_at, tz)} ·{' '}
            {formatTimeRange(headline.starts_at, headline.ends_at, tz)}
          </div>
          <div className="mono">
            {headline.title} · {headline.catalogue_no} · {tzLabel(tz, headline.starts_at)}
          </div>
          <a
            className="btn-ghost"
            style={{ marginLeft: 'auto', flexShrink: 0 }}
            href={api.icsUrl(headline.catalogue_no)}
            aria-label={`Add ${headline.title} on ${formatFullDate(headline.starts_at, tz)} to a calendar`}
          >
            + Add to calendar
          </a>
        </div>
      ) : archived ? null : (
        <div style={UPNEXT_QUIET}>
          <div className="mono dim">Next on air</div>
          <div style={BAND_DATE}>Nothing booked</div>
          <p className="t-body" style={{ maxWidth: '52ch' }}>
            {host.name} has no date in the diary at the moment. Bookings usually go up two to three
            weeks ahead.
          </p>
          <Link className="btn-ghost" to="/schedule" style={{ marginLeft: 'auto', flexShrink: 0 }}>
            The whole schedule
          </Link>
        </div>
      )}

      {laterOn.length > 0 ? (
        <div
          style={{
            padding: '14px clamp(12px, 4vw, 28px)',
            borderBottom: 'var(--bw) solid var(--edge)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div className="mono dim">Also booked</div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {laterOn.map((slot) => (
              <li
                key={slot.catalogue_no}
                className="mono"
                style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}
              >
                <span>{formatFullDate(slot.starts_at, tz)}</span>
                <span>{formatTimeRange(slot.starts_at, slot.ends_at, tz)}</span>
                <span className="dim">{slot.title}</span>
                <a href={api.icsUrl(slot.catalogue_no)} aria-label={`Add ${slot.title} to a calendar`}>
                  + Calendar
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Section
        title="Sets"
        meta={
          episodes.length === 0
            ? 'Nothing broadcast yet'
            : `${episodes.length} ${episodes.length === 1 ? 'episode' : 'episodes'} · newest first`
        }
      />

      {episodes.length === 0 ? (
        <div
          style={{
            padding: '26px clamp(12px, 4vw, 28px) 40px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <p className="t-body">
            {host.name} has not been on air yet, so there is nothing in the archive under their name.
            {headline
              ? ' The first one is booked — the date is above.'
              : ' When the first set is broadcast it appears here.'}
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Link className="btn" to="/archive">
              The rest of the archive
            </Link>
            <Link className="btn-ghost" to="/schedule">
              What is coming up
            </Link>
          </div>
        </div>
      ) : (
        <>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {shown.map((episode) => {
              const loaded =
                player.mode === 'episode' &&
                player.current?.catalogue_no === episode.catalogue_no &&
                player.isPlaying;
              const others = episode.hosts.filter((h) => h.slug !== host.slug);

              return (
                <li className="row" key={episode.catalogue_no}>
                  <button
                    type="button"
                    className="pbtn"
                    aria-label={`${loaded ? 'Pause' : 'Play'} ${episode.title}, ${episode.catalogue_no}`}
                    aria-pressed={loaded}
                    onClick={() => playOne(episode)}
                  >
                    <span className={loaded ? 'pausebars' : 'tri'} aria-hidden="true" />
                  </button>

                  <Artwork
                    color={episode.artwork_color}
                    catalogueNo={episode.catalogue_no}
                    size="sm"
                  />

                  <div className="row__mid">
                    <div className="row__title">
                      <Link to={`/episodes/${encodeURIComponent(episode.catalogue_no)}`}>
                        {episode.title}
                      </Link>
                      {others.length > 0 ? (
                        <span className="row__repeat"> — b2b {hostNames(others)}</span>
                      ) : null}
                    </div>
                    <div className="mono dim">
                      {episode.catalogue_no} · {formatFullDate(episode.broadcast_at, tz)} ·{' '}
                      {formatDuration(episode.duration_seconds)} ·{' '}
                      {episode.has_tracklist ? 'Tracklist' : 'No tracklist'}
                    </div>
                    {episode.genres.length > 0 ? (
                      <ChipList label="Genres">
                        {episode.genres.map((genre) => (
                          <Chip key={genre.slug} color={genre.color_token} to={`/genre/${genre.slug}`}>
                            {genre.name}
                          </Chip>
                        ))}
                      </ChipList>
                    ) : null}
                  </div>

                  <div className="row__state">
                    {episode.is_staff_pick ? (
                      <span className="chip chip--pick">Staff pick</span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>

          {episodes.length > SETS_SHOWN ? (
            <div style={{ padding: '18px clamp(12px, 4vw, 28px) 40px' }}>
              <button
                type="button"
                className="btn-ghost"
                aria-expanded={setsOpen}
                onClick={() => setSetsOpen((open) => !open)}
              >
                {setsOpen
                  ? `Show the first ${SETS_SHOWN} sets`
                  : `Show all ${episodes.length} sets`}
              </button>
            </div>
          ) : (
            <div style={{ padding: '18px clamp(12px, 4vw, 28px) 40px' }}>
              <span className="mono dim">Stream only · no downloads</span>
            </div>
          )}
        </>
      )}
    </>
  );
}
